/**
 * The user menu — the SECOND thing in the shell chrome slot #1154 opened, and
 * one menu for the console and for the portal (#1158 M6).
 *
 * ── why this is in `packages/ui` on its first day ──────────────────────────
 * The bell was written in `apps/console`, discovered to be missing from the
 * portal, and extracted — 87 lines became 39, and every subsequent fix stopped
 * needing to be made twice by somebody who remembered there were two. This is
 * the same component class arriving one item later, so it starts where that one
 * ended up. An app cannot import from another app, so the moment a customer
 * surface needs what the console has there are exactly two outcomes: it moves
 * here, or it gets copied. Copying is the default unless somebody stops it.
 *
 * The portal is the host that has NEITHER today: `apps/portal/src` has no
 * profile page, no user menu and no settings route, which is why #1 records M6
 * as having *"no surface anywhere in the shell"*. The console renders the
 * signed-in identity but offers nothing to change about it, because until
 * core 2026081719 no door in the tree could.
 *
 * ── what this knows, and what it must not ─────────────────────────────────
 * Same contract as the bell, deliberately: it FETCHES nothing and it NAVIGATES
 * nothing. The profile arrives as data, saving is a callback, and any extra
 * destinations are items the host supplies. `@solum/ui` is a control kit — the
 * moment a component in it imports `@solum/sdk` or knows an entity key, it stops
 * being importable by a host with a different data door, which is the portal,
 * always.
 *
 * ── the 2FA badge reads a CLAIM, not a door ───────────────────────────────
 * `aal` is minted by GoTrue when it builds the access token and is never copied
 * from a user-supplied object, so a client may read it about ITSELF: a client
 * faking its own badge deceives nobody but itself. There is deliberately no
 * server read of `auth.mfa_factors` behind this — "who has 2FA" is a
 * security-posture directory with a different and more sensitive authority
 * model, and it must not arrive behind a self-service badge.
 *
 * `aal2` means a second factor was used for this session. `aal1` means it was
 * not — which is NOT the same as "you have no second factor enrolled", and the
 * copy says so rather than making a claim the token does not support.
 *
 * ── an affordance a host should not offer is a prop it does not pass ───────
 * `onSave` is optional. A host with no profile door renders the identity and no
 * form — not a disabled box, and never a no-op handler: a focusable control
 * announced as an editor that does nothing is worse than none.
 *
 * `appearance` is the same invariant applied to the theme control (#1466 slice
 * A). The console's top bar carried a single unlabelled moon glyph — one control
 * cycling three states, so the only way to learn what it does is to press it and
 * watch. Three named radios say what the choices ARE, and the preference is a
 * fact about the person, which is what puts it in this menu rather than beside
 * a notification bell. It arrives as a PROP and not as a hardcoded section
 * because `packages/ui` may not import `useTheme` from a host: the portal passes
 * nothing today and its menu is byte-identical after this change, and whether
 * the portal should offer one is a portal decision, not a side effect of a
 * console PR.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { LogOut, ShieldCheck, ShieldAlert, Check, ChevronDown, ChevronRight, ChevronLeft, X } from 'lucide-react'
import { cx } from '../controls/cx'
import { PersonaPanel } from './PersonaPanel'
import s from './UserMenu.module.css'

/** One persona an actor may act as, as the HOST resolved it (#2328 §2). */
export interface PersonaChoice {
  /** The stable, untranslated identity. Every callback carries THIS. */
  key: string
  /** The localized name. */
  label: string
  /** A short governed description, where the deployment has one. */
  description?: string | null
  /** Declared search synonyms. Never private data, never a person's own text. */
  synonyms?: readonly string[]
}

/**
 * The persona facts a host hands the menu.
 *
 * Three stores, and the split is ROOT ruling 74071 §7: `active` is the host's
 * tab-local selection, `choices` is the governed catalogue, and `pinned` /
 * `recent` / `defaultKey` are personal PREFERENCES from a governed per-user
 * door. This component holds none of them.
 */
export interface UserMenuPersona {
  /** The acting persona's key, or null/'' when nothing is chosen. */
  active: string | null
  /** The personas this actor may activate. */
  choices: readonly PersonaChoice[]
  /** Activate one. The host revalidates; this component decides nothing. */
  onActivate: (key: string) => void
  /** Pinned persona keys, from the governed personal-settings door. */
  pinned?: readonly string[]
  /** Recently used persona keys, most recent first, where history exists. */
  recent?: readonly string[]
  /** The actor's default persona key, where the deployment records one. */
  defaultKey?: string | null
  /** Absent ⇒ the row offers no pin action, never a disabled one. */
  onPin?: (key: string) => void
  onUnpin?: (key: string) => void
  onSetDefault?: (key: string) => void
}

/** The caller's own identity, as either host reads it (core.my_profile). */
export interface UserProfile {
  display_name?: string | null
  email?: string | null
  /** Which provider authenticates them. 'local' where nobody else does. */
  idp?: string | null
  /**
   * Is the display name owned by a directory rather than by the person? True
   * disables the form and says where to change it — a field that accepts an
   * edit the next token refresh silently reverts is worse than one that refuses.
   */
  name_is_idp_owned?: boolean | null
  /** Which fields this deployment says are the principal's to state. */
  settable?: readonly string[] | null
}

/**
 * One host-owned destination in the menu (#2348 §6/§7).
 *
 * Everything past `key`/`label`/`onSelect` is OPTIONAL and additive, which is
 * the whole of what #2348 licenses: *"extend the finite prop contract only for
 * optional row icon/value/description/section/chevron and optional real
 * destinations"*. There is no new store, no SDK import and no second exported
 * surface — a host that passes what it passed today gets what it rendered
 * today, in the region it rendered in.
 *
 * `section` exists because the design orders the menu Identity → Assurance →
 * Persona → Account → Preferences → Sign out, and a flat array cannot say which
 * of the two middle regions a row belongs to. ABSENT means Preferences, because
 * that is where every row the console passes today already sits — a default
 * that moves existing callers is a silent redesign of their menu.
 */
export interface UserMenuItem {
  key: string
  label: string
  onSelect: () => void
  /** A glyph the HOST supplies. This package does not choose icons for a host's route. */
  icon?: ReactNode
  /** What this row currently STATES, where it states something (`Dark`, `⌘K`). */
  value?: string
  /** One governed line under the label. Never invented here. */
  description?: string
  /** Which region the row belongs to. Absent ⇒ Preferences — see above. */
  section?: 'account' | 'preferences'
  /**
   * Draws the disclosure chevron.
   *
   * Only for a row that OPENS something — a panel, a route, a dialog. A chevron
   * on a row that toggles a value in place is a promise of somewhere to go, and
   * the person who follows it lands nowhere.
   */
  chevron?: boolean
}

export interface UserMenuProps {
  profile?: UserProfile | null
  /**
   * The session's own `aal` claim. `undefined` renders NO badge rather than a
   * reassuring one — an absent fact is not a negative fact, and a shield saying
   * "single factor" to somebody who does hold a second one is a false statement
   * about their own security.
   */
  aal?: string | null
  /**
   * What `Secure` does, where the host has somewhere for it to go (#2348 §4).
   *
   * Absent renders the claim and NO action — not a disabled button, and never a
   * handler that opens nothing. A row promising to secure an account and doing
   * nothing is the worst possible affordance on this particular menu.
   */
  onSecure?: () => void
  /** Omit entirely for a host with no profile door — see the header. */
  onSave?: (displayName: string) => void
  /** The server's own refusal sentence, if the last save was refused. */
  error?: string | null
  saving?: boolean
  onSignOut?: () => void
  /** Extra destinations the host owns; the menu renders them and decides nothing. */
  items?: readonly UserMenuItem[]
  /**
   * The host's own theme control. A host that does not offer one does not pass
   * this, and no radiogroup is rendered — see the header.
   */
  appearance?: {
    mode: 'system' | 'light' | 'dark'
    onChange: (mode: 'system' | 'light' | 'dark') => void
  }
  /**
   * The host's own HEADER TREATMENT control (#2347 §4).
   *
   * A SECOND, separately named preference — *"Header treatment — Match theme |
   * Contrast"* — and not a fourth option on `appearance`, because it answers a
   * different question: `appearance` is whether the workspace is light or dark,
   * this is whether its product headers stand apart from it. §1 rules out the
   * combined vocabulary (`light headers`, pickers, sliders) by name.
   *
   * Same invariant as `appearance`: a host that does not offer one does not
   * pass this, and no radiogroup is rendered. Both are bound to ONE
   * `ThemeProvider` state by the host — §4's *"two presentations of one
   * ThemeProvider state, not two stores"* — which the kit cannot enforce
   * because `packages/ui` may not import `useTheme` from a host; the host
   * bindings' own tests carry that arm.
   */
  headerTreatment?: {
    treatment: 'match' | 'contrast'
    onChange: (treatment: 'match' | 'contrast') => void
  }
  /**
   * The accounts this DEVICE has signed in as, and the two ways to leave this
   * session for another one (#1053 4b).
   *
   * Absent on a host that does not offer switching, and then no section is
   * rendered — the same invariant as `appearance` and `onSave`, and the reason
   * the portal's menu is byte-identical after this change.
   *
   * `@solum/ui` is a control kit: this component does not know what an account
   * IS, does not fetch one, and cannot tell you which accounts belong to the
   * same human. It draws the list the host hands it. That is not only the
   * package's rule — it is the security property. The device's own memory of
   * which logins have signed in here is the ONLY evidence this list may rest
   * on; deriving it from `core.app_user.person_id` would offer a stranger's
   * account on a wrong MDM merge, which is why #1053 comment 17850 rules that
   * shape out. See `packages/auth/src/accounts.ts`.
   */
  accounts?: {
    /** Who is signed in now. Rendered as a statement, never as a choice. */
    current?: { userId: string; email: string | null } | null
    /** The rest of the device's roster, most recent first. */
    others: readonly { userId: string; email: string | null }[]
    /** Leave this session and authenticate as that account. */
    onSwitch: (userId: string) => void
    /** Leave this session and authenticate as somebody new. */
    onAddAccount: () => void
  }
  /**
   * The menu's own TRIGGER, handed back to the host (#1466 F-30B, ADR F-30
   * decision 3).
   *
   * An `items` entry that opens a modal has a focus-restoration problem this
   * component cannot solve alone: the menu closes before `onSelect` runs — and
   * it must, because leaving an interactive menu beneath a modal sheet is a
   * worse defect than the one being fixed — so the item that launched the modal
   * is unmounted before the modal captures `document.activeElement`, and closing
   * the modal returns focus to `<body>` (measured in every profile, comment
   * 42636 finding 4).
   *
   * The trigger is the element that SURVIVES the menu closing, so it is the
   * honest restoration target. Exposed rather than resolved by the host from the
   * DOM: a `data-testid` lookup would make a test hook load-bearing in
   * production, and it would break silently the day the attribute moved.
   */
  triggerRef?: RefObject<HTMLButtonElement | null>
  /**
   * The acting persona and the ones this actor may activate (#2328 §2).
   *
   * Absent renders NO persona section and NO persona text in the trigger — the
   * portal has no personas to activate and its menu is byte-identical, which is
   * the same invariant `appearance`, `accounts` and `onSave` already carry.
   * Never a no-op handler: a control announced as a persona switcher that
   * switches nothing is worse than none.
   */
  persona?: UserMenuPersona
  /**
   * Which side the panel opens on. `below` (the default) is right under a top
   * bar; a host that mounts this at the BOTTOM of a column passes `above`,
   * because there is nothing below a footer for the panel to occupy and a
   * browser cannot scroll it into view.
   */
  placement?: 'below' | 'above'
  /**
   * Where a SECOND trigger for this same menu is drawn (#2406 leading designs).
   *
   * The owner's designs put the signed-in user in two places at once: the
   * header trigger (Organization Detail) and the cap at the foot of the
   * navigation rail (the navbar sheet marked LOCKED). Two menus would be the
   * fork this package exists to prevent — two open states, two persona
   * panels, and a sign-out that could be reached twice — so there is ONE
   * menu, and the host hands it a DOM slot to draw its second trigger into.
   *
   * A slot and not a second component because the rail and the header are in
   * different React trees: the menu stays the one owner of its state, its
   * identity derivation and its focus rules, and the cap is portalled into
   * the host's element. Opened from the cap, the panel rises ABOVE it (the cap
   * is at the bottom of a column) and focus moves INTO the panel, because the
   * panel does not follow the cap in DOM order the way it follows the header
   * trigger. Absent or null renders no second trigger at all.
   */
  capSlot?: HTMLElement | null
}

const MODES = ['system', 'light', 'dark'] as const
const MODE_LABEL: Record<(typeof MODES)[number], string> = {
  system: 'System', light: 'Light', dark: 'Dark',
}
/* #2347 §1's finite pair, and the labels it specifies verbatim. `match` first
   because it is the quieter answer and the group reads from calm to emphatic;
   which one is SELECTED is the provider's default, not this order. */
const HEADER_TREATMENTS = ['match', 'contrast'] as const
const HEADER_TREATMENT_LABEL: Record<(typeof HEADER_TREATMENTS)[number], string> = {
  match: 'Match theme', contrast: 'Contrast',
}

/**
 * A real `radiogroup`, roving tabindex and all.
 *
 * Three buttons with `role="radio"` and no roving tabindex would be an ARIA
 * pattern that no automated check flags and that a keyboard user feels
 * immediately: Tab would walk through the group one option at a time while the
 * arrow keys — the thing the role PROMISES — did nothing. So exactly one option
 * is tabbable, the arrows move and select together (the radiogroup convention:
 * selection follows focus), and the wrap is deliberate so the three-item group
 * has no dead end.
 */
function PreferenceGroup<T extends string>({ label, testid, options, labels, value, onChange }: {
  label: string
  testid: string
  options: readonly T[]
  labels: Readonly<Record<T, string>>
  value: T
  onChange: (next: T) => void
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const move = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length
    onChange(options[next]!)
    refs.current[next]?.focus()
  }
  return (
    <div className={s.section} role="radiogroup" aria-label={label}
      data-testid={testid}>
      <span className={s.label}>{label}</span>
      <div className={s.modes}>
        {options.map((m, i) => (
          <button key={m} type="button" role="radio" aria-checked={value === m}
            data-mode={m} className={cx(s.mode, value === m && s.modeOn)}
            tabIndex={value === m ? 0 : -1}
            ref={(el) => { refs.current[i] = el }}
            onClick={() => onChange(m)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1) }
              else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1) }
            }}>
            {labels[m]}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Below this width the persona subpanel REPLACES the root panel (#2348 §2).
 *
 * The design states 760 and the geometry agrees with it rather than the other
 * way round: the panel is 320px and the subpanel is the same, so side by side
 * they need 640px plus the 8px viewport inset at each end and the gap between
 * them — roughly 680px before anything else is on screen. 760 leaves the pair
 * somewhere to sit instead of pinning it to both edges of the window.
 *
 * Read through `matchMedia` and NOTHING else, which is `breakpoints.ts`'s
 * established answer and its reasoning: jsdom ships no `matchMedia` and performs
 * no layout, so a unit rig honestly has no viewport and gets the side-by-side
 * branch — the one a jsdom fixture can assert. The replacing branch is proved in
 * a browser, at a real viewport, where it is the only place it can be proved.
 */
export const PERSONA_STACK_MIN_PX = 760

function useSideBySide(): boolean {
  const query = `(min-width: ${PERSONA_STACK_MIN_PX}px)`
  const [wide, setWide] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
    return window.matchMedia(query).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(query)
    const onChange = () => setWide(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return wide
}

/**
 * One row in the menu — the shape #2348 §50 asks for.
 *
 * *"Customize navigation changed from a bare underlined link to a standard menu
 * row with icon and chevron."* Every host destination draws through here, so
 * there is one row grammar rather than a link, a button and a value line that
 * each learned their own.
 *
 * The chevron is drawn only where the host declared one. `value` sits at the
 * right, and it is in the ACCESSIBLE NAME too: a row whose value is visual only
 * reads as "Persona" to a screen reader, which is the label and not the answer.
 */
function MenuRow({
  label, value, description, icon, chevron, onSelect, testId, ariaHasPopup, expanded, rowRef,
}: {
  label: string
  value?: string | null
  description?: string
  icon?: ReactNode
  chevron?: boolean
  onSelect: () => void
  testId?: string
  ariaHasPopup?: boolean
  expanded?: boolean
  rowRef?: RefObject<HTMLButtonElement | null>
}) {
  return (
    <button type="button" className={s.row} onClick={onSelect} data-testid={testId}
      ref={rowRef as RefObject<HTMLButtonElement> | undefined}
      aria-haspopup={ariaHasPopup ? 'true' : undefined}
      aria-expanded={ariaHasPopup ? Boolean(expanded) : undefined}
      aria-label={value ? `${label}: ${value}` : undefined}>
      {icon && <span className={s.rowIcon} aria-hidden>{icon}</span>}
      <span className={s.rowText}>
        <span className={s.rowLabel}>{label}</span>
        {description && <span className={s.rowDesc}>{description}</span>}
      </span>
      {value && <span className={s.rowValue} aria-hidden>{value}</span>}
      {chevron && <ChevronRight size={14} className={s.rowChevron} aria-hidden />}
    </button>
  )
}

/**
 * The persona surface, headed and dismissible — ONE component, TWO mounts.
 *
 * Adjacent to the root panel above `PERSONA_STACK_MIN_PX`, in place of it
 * below. Composing it rather than writing the narrow case separately is the
 * same argument `PersonaPanel` itself makes about not being on the barrel: two
 * persona surfaces is how the two hosts ended up with two of everything else.
 *
 * Back is LABELLED in both mounts, not only in the replacing one. #2348 §2 asks
 * for it where the panel replaces the root, and Escape covers the keyboard
 * everywhere — but a coarse-pointer tablet at 1024px gets the adjacent panel and
 * has no Escape key, so a dismissal that exists only on a keyboard is not a
 * dismissal for the person holding the device.
 */
function PersonaSubpanel({ persona, onBack }: { persona: UserMenuPersona; onBack: () => void }) {
  return (
    <div className={s.subpanelInner}>
      <div className={s.subhead}>
        <button type="button" className={s.back} onClick={onBack}
          data-testid="user-menu-persona-back" aria-label="Back to the account menu">
          <ChevronLeft size={15} aria-hidden />
          <span>Back</span>
        </button>
      </div>
      <PersonaPanel persona={persona} />
    </div>
  )
}

/**
 * The person's initials — first name and last name — so the trigger is never an
 * empty circle.
 *
 * From the NAME when there is one. Only when there is none does the login
 * address stand in, and then only its LOCAL part: this split the whole address
 * on `@` and `.`, so `c.wilkinson@moonbase.be` became c · wilkinson · moonbase ·
 * be and the avatar read "CB" — the first letter of a name and the first letter
 * of a top-level domain. A domain is where somebody's mail goes, not part of
 * who they are.
 */
export function initials(p: UserProfile | null | undefined): string {
  const pick = (parts: string[]) =>
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '')).toUpperCase()
  const name = (p?.display_name ?? '').trim()
  if (name) return pick(name.split(/\s+/).filter(Boolean)) || '?'
  return pick(loginWords(p?.email).flatMap((word) => word.split('-')).filter(Boolean)) || '?'
}

/**
 * The words of a login address's LOCAL part — one reading, shared by
 * `initials()` and `accountName()` so the avatar and the name beside it can
 * never disagree about who this is.
 *
 * A `+tag` sub-address is a mail rule, not a name: `…dubois+crm` would
 * otherwise end the initials on the C of "crm". A domain is where somebody's
 * mail goes, not part of who they are, so nothing after `@` is read. `.` and
 * `_` separate words; a hyphen does NOT, because it is part of a name
 * ("jean-luc") — `initials()` splits it itself, where it is a letter boundary.
 */
function loginWords(email: string | null | undefined): string[] {
  const local = ((email ?? '').trim().split('@')[0] ?? '').split('+')[0] ?? ''
  return local.split(/[._]+/).filter(Boolean)
}

/**
 * The name the menu shows for this person (#2406 leading designs: the header
 * trigger and the rail cap both read "C. Wilkinson", never the raw address).
 *
 * `display_name` when the profile states one — that is the person's own
 * answer, or their directory's, and it is never rewritten here. Only when
 * there is none is a readable name DERIVED from the login address's local
 * part: "c.wilkinson" -> "C. Wilkinson". A one-letter word is an initial and
 * takes a period; a hyphenated word keeps its hyphen and capitalises each
 * part ("jean-luc" -> "Jean-Luc"). The full address is still shown, on its
 * own line, inside the menu — this is a label, not a claim about identity.
 *
 * Null when there is nothing to derive from, so the caller decides what an
 * unknown person is called rather than this inventing a word.
 */
export function accountName(p: UserProfile | null | undefined): string | null {
  const stated = (p?.display_name ?? '').trim()
  if (stated) return stated
  const words = loginWords(p?.email)
  if (words.length === 0) return null
  const cap = (part: string) => part.charAt(0).toLocaleUpperCase() + part.slice(1)
  return words
    .map((word) => (word.length === 1 ? `${cap(word)}.` : word.split('-').map(cap).join('-')))
    .join(' ')
}

export function UserMenu({
  profile, aal, onSecure, onSave, error, saving, onSignOut, items, appearance, headerTreatment,
  accounts,
  persona,
  triggerRef,
  placement = 'below',
  capSlot,
}: UserMenuProps) {
  /* WHICH trigger opened the menu, or null when it is shut (#2406). One value
     rather than `open` plus an anchor, so "open, anchored to nothing" is not a
     state this component can be in. */
  const [openFrom, setOpenFrom] = useState<'trigger' | 'cap' | null>(null)
  const open = openFrom !== null
  const setOpen = (next: boolean) => setOpenFrom((was) => (next ? (was ?? 'trigger') : null))
  const capRef = useRef<HTMLButtonElement | null>(null)
  /* The cap is at the foot of a column: its panel rises above it. Declared by
     WHERE the cap is, never measured, for the reason the `placement` prop
     gives — a popover that silently changes side is harder to aim at. */
  const effectivePlacement = openFrom === 'cap' ? 'above' : placement
  const panelRef = useRef<HTMLDivElement>(null)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  /* WHICH PANEL IS IN FRONT (#2348 §2).
     One value, not two booleans: "the root is open" and "the persona panel is
     open" as separate flags admits the state where both are false while the menu
     is open, which is a menu showing nothing. */
  const [view, setView] = useState<'root' | 'persona'>('root')
  const sideBySide = useSideBySide()
  const subpanelRef = useRef<HTMLDivElement>(null)
  /* The row that OPENED the subpanel, so Back has somewhere honest to return
     focus to. The same reasoning as `triggerRef` one layer out: the element that
     survives the thing it opened closing is the only truthful restoration
     target. */
  const personaRow = useRef<HTMLButtonElement | null>(null)
  /* Is the persona surface standing IN PLACE of the root, or beside it? One
     expression, read by the effects and by the render, so the two cannot
     disagree about which panel is showing. Declared here rather than beside the
     other derived values below because the focus effect closes over it. */
  const stacked = Boolean(persona) && view === 'persona' && !sideBySide
  const backToRoot = () => { setView('root'); personaRow.current?.focus() }

  const current = profile?.display_name ?? ''
  const idpOwned = profile?.name_is_idp_owned === true
  // The boundary is the SERVER's answer, not this component's. Undefined while
  // it is in flight, so nothing is offered before the server has said what may
  // be offered — rather than guessing and retracting.
  const maySetName = (profile?.settable ?? []).includes('display_name')
  const canEdit = Boolean(onSave) && maySetName && !idpOwned

  /* Escape and an outside click both used to call `setOpen(false)` and nothing
     else. The panel and everything focusable in it then unmount, so
     `document.activeElement` falls to `<body>` and a keyboard user who dismissed
     the menu is returned to the top of the document — the same defect
     `triggerRef` exists to solve one layer out, left unsolved one layer in.
     Escape restores focus to the trigger because the person is still driving
     with the keyboard; an outside CLICK deliberately does not, because they have
     already put focus somewhere by clicking (#1466 A review). */
  const localTrigger = useRef<HTMLButtonElement | null>(null)
  const setTrigger = (el: HTMLButtonElement | null) => {
    localTrigger.current = el
    if (triggerRef) triggerRef.current = el
  }

  useEffect(() => {
    if (!open) return
    /* The cap is portalled OUT of `.wrap`, so a press on it is not "outside"
       even though `.wrap` does not contain it. Without this the mousedown
       closes the menu and the click that follows reopens it — a toggle that
       can never close. */
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (capRef.current?.contains(target)) return
      if (ref.current && !ref.current.contains(target)) setOpen(false)
    }
    /* Escape unwinds ONE layer at a time (#2348): the innermost row disclosure
       first — `PersonaPanel`'s row menu stops the event itself, so it never
       reaches here — then the persona subpanel, then the root. A single press
       that closed the whole stack would throw away the panel a person had
       navigated into to answer one question. */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (view === 'persona') {
        setView('root')
        personaRow.current?.focus()
        return
      }
      /* Back to whichever trigger opened it — the one the person was on. */
      const from = openFrom === 'cap' ? capRef.current : localTrigger.current
      setOpen(false)
      from?.focus()
    }
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open, view, openFrom])

  /* OPENED FROM THE CAP, focus moves INTO the panel. The header trigger's
     panel follows it in DOM order, so Tab walks in — the contract stated at
     the trigger below. The cap's does not (it is portalled into another
     landmark), so without this a keyboard user would open a menu and Tab away
     from it. The first control is the one moved to, which is the APG
     menu-button pattern's own first stop. */
  useEffect(() => {
    if (openFrom !== 'cap') return
    panelRef.current?.querySelector<HTMLElement>('button:not([disabled]), [href], input, [tabindex="0"]')?.focus()
  }, [openFrom])

  /* ── ESCAPING A CLIPPED COLUMN WITHOUT LEAVING THE DOM ──────────────────
 *
   * `above` is the navigation-footer mount, and that column CLIPS: the
   * middle being its only scroller is ruling 72563 §7's own requirement, so
   * `overflow: hidden` on the column is not negotiable. An absolutely
   * positioned 320px panel inside a 220-240px column is therefore cut at the
   * column edge — measured live, Sign out sat one pixel past it and
   * `elementFromPoint` returned the page behind.
   *
   * A React portal would escape it and would also break the contract this
   * component states above its own trigger: *"the panel sits immediately
   * after its trigger in DOM order, so Tab walks into it and Shift+Tab walks
   * back out — it is not modal and must not be trapped."* Moving it to
   * `document.body` makes that false, and nothing would fail loudly.
   *
   * `position: fixed` escapes the clip and keeps the DOM order, so focus,
   * Escape, the outside-click handler and the tab walk are all untouched. It
   * needs coordinates, which is what this measures. Two clamps, both against
   * the viewport rather than the column, because the panel is now positioned
   * against the viewport: it never starts left of 8px, and it never extends
   * past the right edge.
   *
   * `below` uses the same viewport clamp now. A right-aligned absolute panel
   * inherited a fractional overflow from a crowded phone header (390.265px in
   * a 390px viewport), so "right: 0" was not a viewport guarantee. */
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const trigger = openFrom === 'cap' ? capRef.current : localTrigger.current
      const panel = panelRef.current
      if (!trigger || !panel) return
      const t = trigger.getBoundingClientRect()
      /* ── `below` IS MEASURED NOW, AND A SHORT VIEWPORT IS WHY (#2328 §2) ──
       *
       * `below` used to be the plain absolute drop it had always been, with no
       * measurement at all — correct while the panel was an identity block, a
       * theme strip and sign out. #2328 adds the persona subpanel, and at the
       * automotive profile (960x480) the panel then ran past the bottom of the
       * window: measured 480px of viewport, a 56px header, and a panel taller
       * than the 416px left under it, with nothing to scroll it into.
       *
       * So the same clamp the `above` branch already had: give it the room
       * there actually is and let it scroll inside that, which is what
       * `Menu.module.css` takes from Radix for a menu taller than its window.
       * Both axes are measured: align its right edge to the trigger, then clamp
       * the result inside an 8px viewport inset. */
      /* The SUBPANEL is placed against the root panel, not against the trigger
         (#2348 §2 — *"desktop: adjacent, viewport-clamped panel"*). It is
         `position: fixed` for the reason the root panel is: a navigation column
         keeping `overflow: hidden` would clip an absolutely positioned sibling,
         and a portal would break this component's stated DOM-order contract.

         It prefers the side with more room and is then clamped into the viewport
         on both axes, so the pair never hangs off an edge. Below
         `PERSONA_STACK_MIN_PX` there is no subpanel to place — the persona view
         replaces the root — so this runs for the adjacent case alone. */
      const placeSubpanel = () => {
        const sub = subpanelRef.current
        if (!sub) return
        const p = panel.getBoundingClientRect()
        const width = sub.offsetWidth
        const gap = 8
        const roomRight = window.innerWidth - p.right - gap
        const preferred = roomRight >= width || roomRight >= p.left - gap
          ? p.right + gap
          : p.left - gap - width
        sub.style.left = `${Math.max(8, Math.min(preferred, window.innerWidth - width - 8))}px`
        const top = Math.max(8, Math.min(p.top, window.innerHeight - sub.offsetHeight - 8))
        sub.style.top = `${top}px`
        sub.style.maxHeight = `${Math.max(120, window.innerHeight - top - 8)}px`
      }

      if (effectivePlacement !== 'above') {
        const width = panel.offsetWidth
        const left = Math.max(8, Math.min(t.right - width, window.innerWidth - width - 8))
        panel.style.left = `${left}px`
        panel.style.top = `${t.bottom + 8}px`
        panel.style.maxHeight = `${Math.max(120, window.innerHeight - t.bottom - 16)}px`
        placeSubpanel()
        return
      }
      const width = panel.offsetWidth
      const left = Math.max(8, Math.min(t.left, window.innerWidth - width - 8))
      panel.style.left = `${left}px`
      panel.style.bottom = `${Math.max(8, window.innerHeight - t.top + 8)}px`
      /* …and it must FIT. Measured at the automotive frame (960x480): the panel
         is ~300px and the trigger sits at y=430, so opening upward put its top at
         y = -5 and cut the identity block off. Nothing can shrink a panel, so it
         is given the room there actually is and scrolls inside it — the same
         answer Menu.module.css takes from Radix for a menu taller than its
         window. */
      panel.style.maxHeight = `${Math.max(120, t.top - 16)}px`
      placeSubpanel()
    }
    place()
    window.addEventListener('resize', place)
    /* Capture: the column and the page are their own scrollers, and a panel
       fixed to the viewport does not travel with either. */
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, effectivePlacement, openFrom, view])

  // Reset the draft whenever the menu opens, so a refused edit is not still
  // sitting there the next time it is opened.
  // …and the VIEW with it: a menu reopening into the persona subpanel would
  // answer a question nobody asked this time, and hide the identity block that
  // is the reason most people open it.
  useEffect(() => { if (open) { setEditing(false); setName(current); setView('root') } }, [open, current])

  /* A host that stops offering personas mid-session (a sign-out, a switch) must
     not leave the menu parked on a panel with nothing in it. */
  useEffect(() => { if (!persona) setView('root') }, [persona])

  /* FOCUS FOLLOWS THE DISCLOSURE.
     Opening a panel and leaving focus on the row behind it is the defect this
     menu has already paid for twice, one layer out each time: a keyboard user
     presses Tab and walks the panel they just left. The search field is the
     first thing anybody does here when there is one; where the catalogue is too
     short to offer it, Back is the first control in the panel. Queried from the
     panel's own subtree rather than by a document-wide test id, so it cannot
     find another menu's field. */
  useEffect(() => {
    if (!open || view !== 'persona') return
    const scope = (stacked ? panelRef.current : subpanelRef.current)
    const field = scope?.querySelector<HTMLElement>('input[type="search"]')
      ?? scope?.querySelector<HTMLElement>('[data-testid="user-menu-persona-back"]')
    field?.focus()
  }, [open, view, stacked])

  /* The NAME, derived where the profile states none — never the raw address,
     which stays on its own line inside the panel (#2406 leading designs). */
  const label = accountName(profile) ?? 'Signed in'
  /* The ACTING persona's own label, resolved from the key the host handed us —
     never the key itself, which is an identity and not a word anybody reads. */
  const actingPersona = persona
    ? persona.choices.find((choice) => choice.key === (persona.active ?? '')) ?? null
    : null

  /* Host rows, split by the region they declared. `filter` twice rather than a
     reduce, because the absent-section default is a STATEMENT — "Preferences,
     because that is where they render today" — and it should be readable as one
     rather than inferred from an accumulator. */
  const accountItems = (items ?? []).filter((i) => i.section === 'account')
  const preferenceItems = (items ?? []).filter((i) => i.section !== 'account')

  /* ONE identity reading for BOTH triggers (#2406): the header trigger and the
     rail cap say the same name, the same persona line and the same accessible
     name, because they are drawn from the same three values below. */
  const triggerLabel = actingPersona
    ? `Account: ${label}, acting as ${actingPersona.label}`
    : `Account: ${label}`
  const personaLine = (testid: string) => !persona ? null : actingPersona ? (
    <span className={s.whoPersona} data-testid={testid}>{actingPersona.label}</span>
  ) : persona.choices.length > 0 ? (
    /* THE LINE IS ALWAYS THERE when there is something to choose.
       With no persona resolved for this tab the trigger used to be a
       bare address with nothing under it, which read as though
       personas did not exist here. It is an invitation, not a claim:
       it names no persona the tab has not chosen, and it is what the
       menu behind it offers. No choices at all ⇒ no line, never a
       promise the menu cannot keep. */
    <span className={cx(s.whoPersona, s.whoUnchosen)} data-testid={`${testid}-unchosen`}>
      Choose a persona
    </span>
  ) : null

  return (
    <div className={s.wrap} ref={ref}>
      {/* Same disclosure contract as the bell: `aria-haspopup` + `aria-expanded`.
          The panel sits immediately after its trigger in DOM order, so Tab walks
          into it and Shift+Tab walks back out — it is not modal and must not be
          trapped. */}
      {/* #2328 §1 — the header's signed-in user AND active persona are one
          control. The name is hidden by the stylesheet at a compact width and
          the persona indicator is not, because §2 reduces the trigger to
          *"avatar plus persona indicator"* rather than to the avatar alone. The
          accessible name carries BOTH at every width: what a compact viewport
          drops is pixels, never meaning. */}
      <button type="button" ref={setTrigger} className={cx(s.trigger, persona && s.triggerWide)}
        onClick={() => setOpenFrom((was) => (was === 'trigger' ? null : 'trigger'))}
        aria-haspopup="true" aria-expanded={openFrom === 'trigger'}
        aria-label={triggerLabel}
        title={profile?.email ?? undefined}
        data-testid="user-menu-trigger">
        <span className={s.avatar} data-assurance={aal || undefined} aria-hidden>{initials(profile)}</span>
        {persona && (
          <span className={s.who} aria-hidden>
            <span className={s.whoName} data-testid="user-menu-trigger-name">{label}</span>
            {personaLine('user-menu-trigger-persona')}
          </span>
        )}
        <ChevronDown className={s.triggerChevron} size={14} aria-hidden />
      </button>

      {/* THE SECOND TRIGGER — the rail's user cap (#2406 navbar sheet, LOCKED:
          "the user caps the bottom"). Portalled into the host's slot, so it is
          drawn in the navigation column while its state, its name and its
          panel stay this component's. The wrapper is a size container: the
          cap reduces to its avatar when the column collapses to its icon
          wall, and that is a question about the cap's own width, never the
          window's. */}
      {capSlot && createPortal(
        <span className={s.capWrap}>
          <button ref={capRef} type="button" className={s.cap}
            onClick={() => setOpenFrom((was) => (was === 'cap' ? null : 'cap'))}
            aria-haspopup="true" aria-expanded={openFrom === 'cap'}
            aria-label={triggerLabel}
            title={profile?.email ?? undefined}
            data-testid="user-menu-cap">
            <span className={s.avatar} data-assurance={aal || undefined} aria-hidden>{initials(profile)}</span>
            <span className={s.capWho} aria-hidden>
              <span className={s.capName} data-testid="user-menu-cap-name">{label}</span>
              {personaLine('user-menu-cap-persona')}
            </span>
          </button>
        </span>,
        capSlot,
      )}

      {open && (
        <div ref={panelRef} className={s.panel} data-placement={effectivePlacement} data-view={view}
          data-opened-from={openFrom ?? undefined}
          data-has-persona={persona ? '' : undefined}
          role="group" aria-label="Account" data-testid="user-menu-panel">
          {stacked ? (
            /* BELOW 760px the subpanel REPLACES the root (#2348 §2). The same
               component as the adjacent case — one persona surface, mounted in
               two places, which is the whole reason it is composed rather than
               forked. */
            <PersonaSubpanel persona={persona!} onBack={backToRoot} />
          ) : (
          <>
          {/* The SCROLLING middle. Everything above Sign out lives in here, so a
              short viewport scrolls the regions and never the destructive row. */}
          <div className={s.middle} data-testid="user-menu-middle">
          <div className={s.head}>
            <span className={s.headAvatar} aria-hidden>{initials(profile)}</span>
            <span className={s.headCopy}>
              <span className={s.name} data-testid="user-menu-name">{label}</span>
              {profile?.email && <span className={s.email} data-testid="user-menu-email">{profile.email}</span>}
              {actingPersona && (
                <span className={s.headPersona} data-testid="user-menu-current-persona">
                  Acting as <strong>{actingPersona.label}</strong>
                </span>
              )}
              {idpOwned && (
                <span className={s.directoryBadge} data-testid="user-menu-idp-note"
                  title="Your name is maintained by your organisation's directory. Change it there and it reaches us at your next sign-in.">
                  Managed by directory
                </span>
              )}
            </span>
          </div>

          {/* ── the name — part of IDENTITY, and that is why it moved (#2348 §2)
              The ruled order is Identity → Assurance → Persona → Account →
              Preferences → Sign out. The name is the same fact as the name and
              email above it, so it belongs in the same region; it used to sit
              below Appearance, which put a preference between two halves of one
              statement.

              THE UNION with #2347/#2353 is here rather than a choice between
              the two: that lane generalised `Appearance` into `PreferenceGroup`
              and added the governed header-treatment control beside it. Both
              survive, and both move WITH Appearance into the Preferences region
              below — which is #2348 §7's own list ("Theme, the governed
              header-treatment setting from #2347, Customize navigation and
              Keyboard shortcuts"). Nothing of #2347's is dropped; what changed
              is which region draws it. */}
          {canEdit && <div className={s.section}>
            {canEdit && editing ? (
              <form className={s.form}
                onSubmit={(e) => { e.preventDefault(); onSave!(name) }}>
                <label className={s.label} htmlFor="user-menu-name-input">Your name</label>
                <input id="user-menu-name-input" className={s.input} value={name}
                  onChange={(e) => setName(e.target.value)} autoFocus
                  data-testid="user-menu-name-input" />
                <div className={s.actions}>
                  <button type="submit" className={s.primary} disabled={saving}
                    data-testid="user-menu-save">
                    <Check size={13} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className={s.ghost}
                    onClick={() => { setEditing(false); setName(current) }}
                    data-testid="user-menu-cancel">
                    <X size={13} /> Cancel
                  </button>
                </div>
                {/* The SERVER's sentence. This component does not decide who may
                    do this and does not paraphrase a rule it does not own. */}
                {error && <p className={s.error} role="alert" data-testid="user-menu-error">{error}</p>}
              </form>
            ) : (
              <button type="button" className={s.link} onClick={() => setEditing(true)} data-testid="user-menu-edit">
                Change your name
              </button>
            )}
          </div>}

          {/* ── ASSURANCE ── the 2FA badge, rendered only where the claim is
              present, and `Secure` only where the host supplies a real
              destination (#2348 §4). No action is invented: a row that says
              "Secure" and does nothing is worse than a badge that only states. */}
          {aal != null && aal !== '' && (
            <div className={cx(s.factor, aal === 'aal2' ? s.factorOn : s.factorOff)}
              data-testid="user-menu-2fa" data-aal={aal}>
              {/* `data-icon` exists so a test can see WHICH shield this is.
                  Without it, swapping the icon for its opposite left the whole
                  suite green (mutation 2's first run) — the specs asserted the
                  sentence and the `data-aal` attribute, and the icon is the part
                  a sighted person reads first and fastest. An untestable
                  affordance carrying the primary meaning is the same class of
                  gap as an inline `style={{}}` no CSS scan can reach. */}
              {aal === 'aal2'
                ? <ShieldCheck size={14} data-icon="shield-check" aria-hidden />
                : <ShieldAlert size={14} data-icon="shield-alert" aria-hidden />}
              <span className={s.factorText}>
                {aal === 'aal2'
                  ? 'Signed in with two-factor authentication'
                  : 'Signed in with a single factor'}
              </span>
              {onSecure && (
                <button type="button" className={s.factorAct} onClick={() => { setOpen(false); onSecure() }}
                  data-testid="user-menu-secure">Secure</button>
              )}
            </div>
          )}

          {/* ── PERSONA ── a VALUE ROW, not the list (#2348 §5).
              The list used to be inlined here with its own internal scrollbar,
              which is what turned a menu into a long document: the acting
              persona — the one fact most people open this for — was below a
              search field and a scrolling region. Now the row STATES it and
              opens the panel that changes it. */}
          {persona && (
            <div className={`${s.section} ${s.personaSection}`} data-testid="user-menu-persona-row">
              <span className={s.label}>Persona</span>
              <MenuRow
                label="Acting as"
                /* The label the host's catalogue carries, never the key, and
                   nothing at all when the acting key is not in the catalogue —
                   this component does not invent a word from an identity. */
                value={actingPersona?.label ?? null}
                onSelect={() => setView(view === 'persona' ? 'root' : 'persona')}
                testId="user-menu-persona-open"
                ariaHasPopup expanded={view === 'persona'}
                rowRef={personaRow}
                chevron />
            </div>
          )}

          {/* ── accounts on this device ──
              Every entry here is a SIGN-OUT followed by a fresh sign-in: nothing
              is parked, so this menu can offer an account and can never grant
              one. The copy says so, because an affordance that silently ends the
              current session is a surprise the second time and a lost draft the
              first. */}
          {accounts && (
            <div className={s.section} data-testid="user-menu-accounts">
              <span className={s.label}>Account</span>
              <div className={s.accounts}>
                {accounts.current && (
                  <div className={cx(s.account, s.accountOn)} data-testid="user-menu-account-current">
                    <span className={s.accountWho}>{accounts.current.email ?? 'Signed in'}</span>
                    <span className={s.accountNote}>Signed in</span>
                  </div>
                )}
                {accounts.others.map((a) => (
                  <button key={a.userId} type="button" className={s.account}
                    data-testid="user-menu-account-switch" data-user-id={a.userId}
                    onClick={() => { setOpen(false); accounts.onSwitch(a.userId) }}>
                    <span className={s.accountWho}>{a.email ?? a.userId}</span>
                    <span className={s.accountNote}>Sign in again to switch</span>
                  </button>
                ))}
                <button type="button" className={s.account}
                  data-testid="user-menu-account-add"
                  onClick={() => { setOpen(false); accounts.onAddAccount() }}>
                  <span className={s.accountWho}>Use another account</span>
                  <span className={s.accountNote}>Signs you out of this one first</span>
                </button>
              </div>
            </div>
          )}

          {/* Host destinations that are about the ACCOUNT — `Account settings`
              and its kin — sit with the accounts they are about. A host that
              declares no section gets the Preferences region below, which is
              where every row it passes today already renders. */}
          {accountItems.length > 0 && (
            <div className={s.section} data-testid="user-menu-account-items">
              {accountItems.map((i) => (
                <MenuRow key={i.key} label={i.label} value={i.value} description={i.description}
                  icon={i.icon} chevron={i.chevron}
                  onSelect={() => { setOpen(false); i.onSelect() }}
                  testId={`user-menu-item-${i.key}`} />
              ))}
            </div>
          )}

          {/* ── PREFERENCES ── what this person has CHOSEN, as opposed to what
              is true about them. Appearance leads because it is the one this
              component owns; `Customize navigation` and `Keyboard shortcuts`
              arrive as host rows, and only where the host has a real action to
              bind — never a disabled row. */}
          {(appearance || headerTreatment || preferenceItems.length > 0) && (
            <div className={s.section} data-testid="user-menu-preferences">
              <span className={s.label}>Preferences</span>
              {/* BOTH governed preference groups, in #2347's own order:
                  Appearance chooses whether the workspace is light or dark, and
                  Header treatment is a refinement OF that surface. `Appearance`
                  became the generic `PreferenceGroup` in #2353 and this renders
                  it under its own label; a host that offers neither passes
                  neither and this region collapses. */}
              {appearance && (
                <PreferenceGroup label="Appearance" testid="user-menu-appearance"
                  options={MODES} labels={MODE_LABEL}
                  value={appearance.mode} onChange={appearance.onChange} />
              )}
              {headerTreatment && (
                <PreferenceGroup label="Header treatment" testid="user-menu-header-treatment"
                  options={HEADER_TREATMENTS} labels={HEADER_TREATMENT_LABEL}
                  value={headerTreatment.treatment} onChange={headerTreatment.onChange} />
              )}
              {preferenceItems.map((i) => (
                <MenuRow key={i.key} label={i.label} value={i.value} description={i.description}
                  icon={i.icon} chevron={i.chevron}
                  onSelect={() => { setOpen(false); i.onSelect() }}
                  testId={`user-menu-item-${i.key}`} />
              ))}
            </div>
          )}
        </div>

        {/* SIGN OUT sits OUTSIDE the scrolling middle (#2348 §8): it is the only
            destructive row and it must stay reachable on a short viewport
            without scrolling past everything else to find it. */}
        {onSignOut && (
          <div className={s.foot}>
            <button type="button" className={s.signOut} onClick={() => { setOpen(false); onSignOut() }}
              data-testid="user-menu-sign-out">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
        </>
      )}
        </div>
      )}
      {/* The ADJACENT persona panel (#2348 §2). Inside `.wrap`, so the
          outside-click handler that closes this menu counts a click in here as
          INSIDE — a subpanel that dismissed the menu when you used it would be
          the defect the row menu's own `stopPropagation` already avoids one
          layer in. */}
      {open && persona && view === 'persona' && sideBySide && (
        <div ref={subpanelRef} className={s.subpanel} data-testid="user-menu-persona-panel"
          role="group" aria-label="Choose a persona">
          <PersonaSubpanel persona={persona} onBack={backToRoot} />
        </div>
      )}
    </div>
  )
}
